<script setup lang="ts">
import { computed } from 'vue'

import type { InsightConfig } from '../../../models/insightConfig.types'
import type { InsightExecutionForm } from '../../../models/workbench.types'

import { useI18n } from '@/shared/i18n'
import { Alert } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Modal } from '@/shared/ui/modal'
import { Select, type SelectOption } from '@/shared/ui/select'
import { Switch } from '@/shared/ui/switch'

const props = defineProps<{
  open: boolean
  form: InsightExecutionForm
  configs: readonly InsightConfig[]
  canPersist: boolean
  loading: boolean
  validationKey: string | null
}>()
const emit = defineEmits<{
  'update:open': [value: boolean]
  'update:form': [value: InsightExecutionForm]
  'dry-run': []
  persist: []
}>()

const { t } = useI18n()
const configOptions = computed<SelectOption<number>[]>(() =>
  props.configs.flatMap((config) =>
    config.id === undefined ? [] : [{ value: config.id, label: config.label || config.enum }],
  ),
)
const openModel = computed({ get: () => props.open, set: (value) => emit('update:open', value) })

function field<K extends keyof InsightExecutionForm>(key: K) {
  return computed({
    get: () => props.form[key],
    set: (value: InsightExecutionForm[K]) => emit('update:form', { ...props.form, [key]: value }),
  })
}
const configId = computed({
  get: () => props.form.config_id,
  set: (value: number | null) => {
    const config = props.configs.find((item) => item.id === value)
    emit('update:form', {
      ...props.form,
      config_id: value,
      config_enum: config?.enum ?? props.form.config_enum,
    })
  },
})
const configEnum = field('config_enum')
const scheduleId = field('schedule_id')
const force = field('force')
const notify = field('notify')
</script>

<template>
  <Modal v-model="openModel" size="lg" :title="t('insights.sysadmin.execute.title')">
    <form class="execute-form grid gap-md" @submit.prevent="emit('dry-run')">
      <Select
        v-model="configId"
        :options="configOptions"
        :label="t('insights.sysadmin.execute.config')"
        clearable
      />
      <Input v-model="configEnum" :label="t('insights.sysadmin.execute.enum')" />
      <Input
        v-model="scheduleId"
        :label="t('insights.sysadmin.execute.schedule')"
        type="number"
        :min="1"
      />
      <div class="execute-form__switches flex flex-wrap gap-lg">
        <Switch v-model="force" :label="t('insights.sysadmin.execute.force')" />
        <Switch v-model="notify" :label="t('insights.sysadmin.execute.notify')" />
      </div>
      <Alert v-if="validationKey" tone="warning">{{ t(validationKey) }}</Alert>
    </form>

    <template #footer>
      <Button variant="ghost" :disabled="loading" @click="emit('update:open', false)">
        {{ t('insights.sysadmin.execute.cancel') }}
      </Button>
      <Button :loading="loading" @click="emit('dry-run')">
        {{ t('insights.sysadmin.execute.dryRun') }}
      </Button>
      <Button variant="soft" :disabled="!canPersist || loading" @click="emit('persist')">
        {{ t('insights.sysadmin.execute.persist') }}
      </Button>
    </template>
  </Modal>
</template>
